import React, { useEffect } from 'react';
import { PDFDocument, rgb } from 'pdf-lib';
import { saveAs } from 'file-saver';
import fontkit from '@pdf-lib/fontkit';

const CustomizePDF = ({ data }) => {

    const handleGeneratePDF = async () => {
        try {
            // Fetch the existing PDF document
            const response = await fetch('./res/SurferMonkey Transaction Report.pdf');
            if (!response.ok) {
                throw new Error(`Failed to fetch PDF: ${response.statusText}`);
            }

            const companyName = sessionStorage.getItem('companyName');

            const fontTitle = 40;
            const titleSub = "Autogenerated transaction report by SurferMonkey";
            const requestedBy = "Requested by: " + "\n" + sessionStorage.getItem('username');
            const titleFontSub = 25;
            const fontDate = 18;

            const existingPdfBytes = await response.arrayBuffer();
            const pdfDoc = await PDFDocument.load(existingPdfBytes);
            const pdfDocTemplate = await PDFDocument.load(existingPdfBytes);

            // Register fontkit instance with pdf-lib
            pdfDoc.registerFontkit(fontkit);

            // Load the Source Sans Pro font (ensure the path is correct)
            const fontBytes = await fetch('./res/fonts/static/SourceSans3-SemiBold.ttf').then(res => res.arrayBuffer());
            const customFont = await pdfDoc.embedFont(fontBytes);

            const fontBytesSubTitle = await fetch('./res/fonts/static/SourceSans3-Regular.ttf').then(res => res.arrayBuffer());
            const customFontSubtitle = await pdfDoc.embedFont(fontBytesSubTitle);

            const fontBytesPar = await fetch('./res/fonts/static/SourceSans3-Regular.ttf').then(res => res.arrayBuffer());
            const customFontsPar = await pdfDoc.embedFont(fontBytesPar);

            // Customize the cover page
            const coverPage = pdfDoc.getPage(0);

            // Center the title on the cover page
            const textWidthSub1 = customFont.widthOfTextAtSize(companyName, fontTitle);
            const centeredXSub1 = (coverPage.getWidth() - textWidthSub1) / 2;

            coverPage.drawText(companyName, {
                x: centeredXSub1,
                y: coverPage.getHeight() - 280,
                size: fontTitle,
                font: customFont,
                color: rgb(1, 1, 1),
            });

            // Center the subtitle on the cover page
            const textWidth = customFontSubtitle.widthOfTextAtSize(titleSub, titleFontSub);
            const centeredX = (coverPage.getWidth() - textWidth) / 2;

            coverPage.drawText(titleSub, {
                x: centeredX,
                y: coverPage.getHeight() - 400,
                size: titleFontSub,
                font: customFontSubtitle,
                color: rgb(216 / 255, 216 / 255, 216 / 255),
            });

            // Date and time
            const currentDate = new Date();
            const date = currentDate.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });

            const time = currentDate.toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });

            const dateTimeSubtitle = `${date} - ${time}`;
            const textWidthSubtitle = customFontsPar.widthOfTextAtSize(dateTimeSubtitle, fontDate);
            const centeredXSubtitle = (coverPage.getWidth() - textWidthSubtitle) / 2;

            coverPage.drawText(dateTimeSubtitle, {
                x: centeredXSubtitle,
                y: coverPage.getHeight() - 430,
                size: fontDate,
                font: customFontsPar,
                color: rgb(1, 1, 1),
            });

            // Requested by
            coverPage.drawText(requestedBy, {
                x: 38,
                y: coverPage.getHeight() - 750,
                size: fontDate,
                font: customFontsPar,
                color: rgb(216 / 255, 216 / 255, 216 / 255),
            });


            // Start with the first page as the current page
            let currentPage = pdfDoc.getPage(1);
            // Handle the body pages
            const pairPerPages = 10;

            // Handle last page
            const lastPageTemplate = pdfDoc.getPage(2);
            // Remove last page from template
            pdfDoc.removePage(2);

            // Function to get blockchain name based on blockchainid
            const getBlockchainName = (blockchainId) => {
                switch (blockchainId) {
                    case 2:
                        return "Polygon";
                    case 3:
                        return "Avalanche";
                    case 4:
                        return "IOTA";
                    default:
                        return "Unknown Blockchain";
                }
            };

            // Group the data by blockchainid
            const groupedData = data.reduce((acc, row) => {
                const blockchainId = row.sources[0].blockchainid;
                if (!acc[blockchainId]) {
                    acc[blockchainId] = [];
                }
                acc[blockchainId].push(row);
                return acc;
            }, {});

            // Iterate through each blockchain group
            for (const blockchainId in groupedData) {
                const blockchainName = getBlockchainName(parseInt(blockchainId));
                const rows = groupedData[blockchainId];

                for (let index = 0; index < rows.length; index++) {
                    const row = rows[index];

                    if (index % pairPerPages === 0) {
                        // Clone the body page template and add it as a new page
                        const [clonedPage] = await pdfDoc.copyPages(pdfDoc, [1]);
                        currentPage = pdfDoc.addPage(clonedPage);

                        // Add a subtitle to the page indicating the blockchain
                        currentPage.drawText(`${blockchainName} Transactions`, {
                            x: 50,
                            y: currentPage.getHeight() - 80,
                            size: 18,
                            font: customFontsPar,
                            color: rgb(216 / 255, 216 / 255, 216 / 255),
                        });

                        currentPage.drawText("Report for: "+companyName, {
                            x: 380,
                            y: currentPage.getHeight() - 80,
                            size: 18,
                            font: customFontsPar,
                            color: rgb(216 / 255, 216 / 255, 216 / 255),
                        });
                    }

                    const baseYPosition = currentPage.getHeight() - (150 + 60 * (index % pairPerPages));
                    const labelXPosition = 50;
                    const hashXPosition = 150;

                    currentPage.drawText(`Source Hash ${index + 1}:`, {
                        x: labelXPosition,
                        y: baseYPosition,
                        size: 12,
                        color: rgb(0, 0, 0),
                    });

                    currentPage.drawText(row.sources[0].txhash, {
                        x: hashXPosition,
                        y: baseYPosition,
                        size: 12,
                        color: rgb(0, 0, 0),
                    });

                    currentPage.drawText(`Target Hash ${index + 1}:`, {
                        x: labelXPosition,
                        y: baseYPosition - 20,
                        size: 12,
                        color: rgb(0, 0, 0),
                    });

                    currentPage.drawText(row.targets[0].txhash, {
                        x: hashXPosition,
                        y: baseYPosition - 20,
                        size: 12,
                        color: rgb(0, 0, 0),
                    });
                }
            }

            // Add the last page from the template
            pdfDoc.addPage(lastPageTemplate);

            // Remove template page
            pdfDoc.removePage(1);


            // Save the customized PDF
            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            saveAs(blob, companyName+"-Oversight-Report"+'.pdf');
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Failed to generate PDF. Please check the console for details.');
        }
    };

    useEffect(() => {
        document.getElementById('generate-pdf-button').addEventListener('click', handleGeneratePDF);
        return () => {
            document.getElementById('generate-pdf-button').removeEventListener('click', handleGeneratePDF);
        };
    }, [data]);

    return <button id="generate-pdf-button" style={{ display: 'none' }}>Generate PDF</button>;
};

export default CustomizePDF;
